import { useMutation, useQueryClient } from "@tanstack/react-query";
import { chatClient } from "@core/clients";
import { MarkOrderChatReadPayload, MarkOrderChatReadResult, OrderChat, OrderChatUnreadSummary } from "@core/types";
import { useAuth } from "../../contexts/auth-context";
import { orderChatQueryKeys } from "./keys";
import { maxIso } from "./timestamps";

const writeCustomerLastReadIntoSummary = (
  prev: OrderChatUnreadSummary | undefined,
  chatId: number,
  orderId: number | undefined,
  customerLastReadAt: string,
): OrderChatUnreadSummary | undefined => {
  const items = prev?.items ?? [];
  const idx = items.findIndex((it) => it.chatId === chatId);

  if (idx === -1) {
    if (!orderId) return prev;
    return {
      items: [
        ...items,
        { chatId, orderId, lastMessageAt: null, customerLastReadAt, unreadCount: 0 },
      ],
    };
  }

  const existing = items[idx];
  const next = items.slice();
  next[idx] = {
    ...existing,
    customerLastReadAt: maxIso(existing.customerLastReadAt, customerLastReadAt) ?? customerLastReadAt,
    unreadCount: 0,
  };
  return { items: next };
};

const writeCustomerLastReadIntoChat = (
  prev: OrderChat | null | undefined,
  customerLastReadAt: string,
): OrderChat | null | undefined => {
  if (!prev) return prev;
  return {
    ...prev,
    customerLastReadAt: maxIso(prev.customerLastReadAt, customerLastReadAt) ?? customerLastReadAt,
  };
};


const useMarkOrderChatReadMutation = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const customerId = user ? Number(user.id) || null : null;

  const applyRead = (chatId: number, orderId: number | undefined, customerLastReadAt: string) => {
    if (customerId) {
      queryClient.setQueryData<OrderChatUnreadSummary>(
        orderChatQueryKeys.unreadSummary(customerId),
        (prev) => writeCustomerLastReadIntoSummary(prev, chatId, orderId, customerLastReadAt),
      );
    }
    if (orderId) {
      queryClient.setQueryData<OrderChat | null>(
        orderChatQueryKeys.byOrder(orderId),
        (prev) => writeCustomerLastReadIntoChat(prev, customerLastReadAt) ?? null,
      );
    }
  };

  return useMutation<MarkOrderChatReadResult, unknown, MarkOrderChatReadPayload>({
    mutationKey: ["chat", "mark-read"],
    mutationFn: (payload) => chatClient.markOrderChatRead(payload),
    onMutate: ({ chatId, orderId }) => {
      applyRead(chatId, orderId, new Date().toISOString());
    },
    onSuccess: ({ chatId, customerLastReadAt }, { orderId }) => {
      if (!customerLastReadAt) return;
      applyRead(chatId, orderId, customerLastReadAt);
    },
  });
};

export default useMarkOrderChatReadMutation;
